import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Crosshair } from 'lucide-react';
import { getCollectionsWorkspace, type ExpectedCollectionsSummary } from '../collections/collectionsApi';
import { apiErrorMessage } from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import { facilityWorkspacePath } from '../routes';

/**
 * WS7 (rows 3.2, 3.5) — expected versus actual for the recent periods: what the released tape and
 * the active scenario said the period would collect, against the tape's own figures and bank cash
 * once the close statement exists. Projection variances are informational; they never block a close.
 */

interface Props {
  facilityId: number;
  currency?: string | null;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const Card = styled.section`
  padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; margin-top: .9rem; font-size: 12px; font-variant-numeric: tabular-nums; }
  th { text-align: left; padding: .45rem .5rem; color: #7dd3fc; font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: .45rem .5rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  .muted { color: #94a3b8; } .out { color: #fda4af; } .in { color: #6ee7b7; }
  .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
  a { color: #7dd3fc; }
`;
const money = (v: unknown) => v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });

interface Row { periodId: number; label: string; expected: ExpectedCollectionsSummary | null; lines: Array<Record<string, any>>; tapeTotal: number | null; bankTotal: number | null }

const ExpectedVsActualCard: React.FC<Props> = ({ facilityId, currency, onFeedback }) => {
  const [rows, setRows] = useState<Row[] | null>(null);
  const load = useCallback(async () => {
    try {
      const ws = await getCollectionsWorkspace(facilityId) as Record<string, any>;
      const periods: Array<Record<string, any>> = (ws.periods || ws.recentPeriods || []).slice(0, 6);
      const out: Row[] = [];
      for (const p of periods) {
        const detail = p.expectedCollections ? p : (await getCollectionsWorkspace(facilityId, p.id) as Record<string, any>).period || p;
        const projected = detail.statement?.run?.result?.projected || detail.statement?.projected || null;
        out.push({ periodId: p.id, label: p.period_label || p.periodLabel, expected: detail.expectedCollections || null, lines: projected?.lines || [],
          tapeTotal: detail.statement?.run?.tape_total ?? null, bankTotal: detail.statement?.run?.bank_total ?? null });
      }
      setRows(out);
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Expected vs actual could not be loaded.') });
      setRows([]);
    }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);

  if (!rows) return null;
  const any = rows.some(r => r.expected?.projected);
  return (
    <Card aria-label="Expected vs actual">
      <h3><Crosshair size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Expected vs actual collections</h3>
      <p className="sub">{any ? 'Per period: what the released tape and the active scenario projected, against the tape\'s own figures and bank cash once the period closed.'
        : 'No period has a projection yet. Project a period from its Collections tab to see expected against actual here.'}</p>
      {any && <table>
        <thead><tr><th>Period</th><th className="num">Expected total</th><th className="num">Tape total</th><th className="num">Bank total</th><th>Components outside tolerance</th></tr></thead>
        <tbody>
          {rows.map(r => <tr key={r.periodId}>
            <td><Link to={`${facilityWorkspacePath(facilityId, 'collections')}?period=${r.periodId}`}>{r.label}</Link></td>
            <td className="num">{r.expected?.projected ? money(r.expected.total) : <span className="muted">not projected</span>}</td>
            <td className="num">{money(r.tapeTotal)}</td>
            <td className="num">{money(r.bankTotal)}</td>
            <td>{r.lines.length === 0 ? <span className="muted">{r.expected?.projected ? 'no close statement yet' : '—'}</span>
              : <>
                {r.lines.filter(l => l.state === 'OUTSIDE_TOLERANCE').length === 0 ? <span className="in">all within tolerance</span>
                  : r.lines.filter(l => l.state === 'OUTSIDE_TOLERANCE').map(l => <span key={l.direction + l.component} className="out" style={{ display: 'block' }}>{l.component}: {money(l.variance)} {currency || ''}</span>)}
                {/* F-MK-018: an actual the tape cannot derive is unknown, never a 100% miss. */}
                {r.lines.filter(l => l.state === 'NOT_DERIVABLE').map(l => <span key={'nd' + l.direction + l.component} className="muted" style={{ display: 'block' }} title={l.note}>{l.component}: not derivable{l.component === 'Scheduled principal' ? ', no prior tape' : ''}</span>)}
              </>}</td>
          </tr>)}
        </tbody>
      </table>}
      <div className="boundary">Projected is never actual: a projection books nothing, a projected waterfall is never finalised, and the first run on matched bank cash supersedes it.</div>
    </Card>
  );
};

export default ExpectedVsActualCard;
